import 'reflect-metadata';

import type { NextPage } from 'next'
import { useEffect, useState } from 'react'
import { API, Auth} from 'aws-amplify';
import { DIContainer } from '../packages/idn-media-framework-tsyringe/DIContainer'
import { IEventBus } from '../packages/idn-media-framework-abstraction/IEventBus'
import { CompositionRoot } from '../src/CompositionRoot'
import { IAuthenticationService } from '../packages/idn-media-framework-abstraction/IAuthenticationService';
import { OAuthFlow } from '../components/helpers/OAuthFlow';
import { ILogger } from '../packages/idn-media-framework-abstraction/ILogger';

import LoggedIn from '@/components/Auth/LoggedIn';

import { getIdentityType, userIdenityType } from '@/utility/identityType';
import { useRouter } from 'next/router';

import { I18n } from 'aws-amplify';
import { CustomErrors } from '@/constants/customErrorStrings';

import { CognitoHostedUIIdentityProvider } from "@aws-amplify/auth";

import checkForceAuth from '../components/helpers/checkForceAuth';
import LoadingPage from '@/components/LoadingPage';
import LandingPage from '@/components/Auth/LandingPage';
import { getOnboardingToggle } from '@/utility/onboarding';

const handleIdPLogin = (identity_provider: any) => {
  const logger = DIContainer.Resolve<ILogger>('ILogger');
  // Store redirect_uri/authorization_code in local storage to be used to later
  let queryStringParams = new URLSearchParams(window.location.search);
  let redirect_uri = queryStringParams.get('redirect_uri');
  let authorization_code = queryStringParams.get('authorization_code');
  let clientState = queryStringParams.get('state');

  if (redirect_uri) {
    logger.debug('Storing redirect_uri to storage:', redirect_uri);
    localStorage.setItem(`client-redirect-uri`, redirect_uri);
  }
  if (authorization_code) {
    logger.debug('Storing authorization_code to storage:', authorization_code);
    localStorage.setItem(`authorization_code`, authorization_code);
  }
  if (clientState) {
    logger.debug('Storing clientState to storage:', clientState);
    localStorage.setItem(`client-state`, clientState);
  }
  
  localStorage.setItem(`social_identity_provider`, identity_provider);

  Auth.federatedSignIn({ provider: identity_provider });
}  

let isInitialized = false;
const Home: NextPage = () => {

  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const [identity, setIdentity] = useState('');
  const [alertMsgSocmed, setAlertMsgSocmed] = useState('');

  const [user, setUser] = useState<null | any>(null); // state for cognitoUser object

  const [isNewPasswordRequiredChallange, setIsNewPasswordRequiredChallange] = useState(false);
  // set loading state on first page load 
  const [isLoadingPage, setIsLoadingPage] = useState(true);

  const router = useRouter();

  const handleIdpRelogin = async () => {
    setIsLoadingPage(true);

    let identity_provider = localStorage.getItem('social_identity_provider');
    if (identity_provider == CognitoHostedUIIdentityProvider.Google) {
      await Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Google });
    } else if (identity_provider == CognitoHostedUIIdentityProvider.Facebook) {
      await Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Facebook });
    } else if (identity_provider == CognitoHostedUIIdentityProvider.Apple) {
      await Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Apple });
    } else {
      setIsLoadingPage(false);
    }
  }

  const init = async () => {
    if(isInitialized) {
      setIsLoadingPage(false);
      // return
    };

    await CompositionRoot.InitializeApplication();

    const eventBus = DIContainer.Resolve<IEventBus>('IEventBus');
    await eventBus.CreateTopic('LoginForm');

    await checkForceAuth();

    const loginFormSubscription = eventBus.Subscribe('LoginForm', async e => {

      if(e.eventName === 'SocialLoginButtonClicked'){
        handleIdPLogin(e.data);
      }
    });

    const authenticationServiceSubscription = eventBus.Subscribe('IAuthenticationService', async e => {
      if(e.eventName === 'signIn'){
        setUser(e.data);

        try {
          setIsLoadingPage(true);
          const OAuthFlowSignIn = await new OAuthFlow().handleSignIn();

          const extraReq = {
            headers: {
              Authorization: `Bearer ${(await Auth.currentSession())
                .getAccessToken()
                .getJwtToken()}`,
            },
          };
          
          const response = await API.get('profile', '/detail', extraReq);

          // Fetch onboarding toggle
          const url = `/api/config/onboarding-toggle`;
          const config = await fetch(url)
            .then((response) => response.json())
            .catch((e) => console.log(e));

          // Get toggle onboarding on/off
          const toggle = getOnboardingToggle(response.data, config);
  
          // show logout button on sso broker when not part of any OAuth flow, otherwise redirect to client web
          if (OAuthFlowSignIn?.isOAuthFlow === false) {
            // set loading state false and show logout button if user login directly from sso broker 
            setIsLoadingPage(false);
            if (response?.data?.is_onboarding === false && router.pathname !== '/onboarding' && toggle === 'on') {
              router.push(`/onboarding`)
            } else {
              setIsLoggedIn(true);
            }
          } else if (OAuthFlowSignIn?.isOAuthFlow === true) {
            // show loading when user redirect to client
            setIsLoadingPage(true);

            if (OAuthFlowSignIn.redirectUrl) {
              if (response?.data?.is_onboarding === false && toggle === 'on') {
                const encodedUrl = encodeURIComponent(OAuthFlowSignIn.redirectUrl);
                window.location.replace(`/onboarding?redirect=${encodedUrl}`)
              } else {
                window.location.replace(OAuthFlowSignIn.redirectUrl)
              }
            }
          }
        } catch (error: any) {
          // show logout button on sso broker when authorization_code invalid
          if (error.response?.data == 'Invalid authorization_code') {
            setIsLoggedIn(true);
            setIsLoadingPage(false);
          }
        }
      } else if (e.eventName === 'signOut') {
        setIsLoggedIn(false);
        setUser(null);
        setIsNewPasswordRequiredChallange(false);
      } else {
        setIsLoadingPage(false);
      }
    })

    const authenticationService = DIContainer.Resolve<IAuthenticationService>('IAuthenticationService');
    await authenticationService.CheckLoggedIn();

    if (router.query.error_description && /already.found.an.entry.for.username/gi.test(
      router.query.error_description.toString(),
    )
    ) {
      handleIdpRelogin();
    }
  }

  const handleIdpSignIn = async (providerName: any) => {
    const logger = DIContainer.Resolve<ILogger>('ILogger');
    logger.debug('SocialLoginButton clickHandler called')

    const eventBus = DIContainer.Resolve<IEventBus>('IEventBus');
    eventBus.Dispatch('LoginForm', {eventName: 'SocialLoginButtonClicked', 
      data: providerName, message: ''});
  };

  useEffect(() => {
    // checking when error message from facebook exist
    if (router.query.error_description) {
      const errorDesc = router.query.error_description.toString().trim();

      // handle email permission disabled by user
      if (errorDesc == CustomErrors.DISABLE_EMAIL_PERMISSION_FACEBOOK || errorDesc == CustomErrors.DISABLE_EMAIL_PERMISSION_FACEBOOK_PRESIGNUP_TRIGGER || errorDesc == CustomErrors.USER_ALREADY_DELETED || errorDesc == CustomErrors.USER_ALREADY_DELETED_REGISTER_IDNACCOUNT || errorDesc == CustomErrors.USER_ALREADY_DELETED_SIGNIN_IDNACCOUNT) {
        setAlertMsgSocmed(I18n.get(errorDesc));
        // handle when user canceled login process on fb and apple, or hide error for linked user
      } else if(errorDesc == CustomErrors.USER_ALREADY_EXIST_REGISTER_IDNACCOUNT) {
        if (getIdentityType(identity) == userIdenityType.PHONE_NUMBER) {
          setAlertMsgSocmed(I18n.get(CustomErrors.PHONE_NUMBER_ALREADY_EXIST_REGISTER));
        } else {
          setAlertMsgSocmed(I18n.get(CustomErrors.EMAIL_ALREADY_EXIST_REGISTER));
        }
      } else if (/password.reset.required.for.the.user/gi.test(router.query.error_description.toString())) {
        setAlertMsgSocmed(CustomErrors.SOCMED_SIGNIN_RESET_REQUIRED);
      } else if (errorDesc == CustomErrors.ACCESS_DENIED_FACEBOOK || errorDesc == CustomErrors.CANCELLED_AUTHORIZE_APPLE || /already.found.an.entry.for.username/gi.test(router.query.error_description.toString())) {
        return;
      } else if (errorDesc.includes("kamu melebihi batas device")) {
        setAlertMsgSocmed(
          "Akun Anda telah mencapai batas maksimum login. Keluar dari perangkat lain untuk melanjutkan."
        );
      } else {
        setAlertMsgSocmed(CustomErrors.DEFAULT_MSG);
      }
    }
  }, [router.isReady, router.query.error, router.query.error_description]);

  const handleLogout = async () => {
    const service = DIContainer.Resolve<IAuthenticationService>('IAuthenticationService');
    await service.Logout();

    await router.push('/signout');

    setIsLoggedIn(false);
    setUser(null);
    setIsNewPasswordRequiredChallange(false);

    // const eventBus = DIContainer.Resolve<IEventBus>('IEventBus');
    // eventBus.Dispatch('IAuthenticationService', {eventName: 'signOut', data: user, message: ''})
  };

  useEffect(()=> {
    init();    
    isInitialized = true;    
  }, [])

  useEffect(() => {
    if (isLoggedIn) router.push('/my-account');
  }, [isLoggedIn, router.push, router.asPath])

  return (
    <>
      {(isLoggedIn || isLoadingPage || isLoggedIn === undefined) && (
        <LoadingPage />
      )}
      {(isLoggedIn === false && isLoadingPage === false) && (
        <LandingPage
          handleIdpSignIn={handleIdpSignIn}
          alertMessageSocmed={alertMsgSocmed}
        />
      )}
    </>
  )
}

export default Home
